import { memo, useCallback, useEffect, useRef, useState, useMemo } from "react";
import ReactECharts, { EChartsOption } from "echarts-for-react";
import { getStackedChartQuery } from "service/proqueryService";
import { DEFAULT_ZOOM_OPTION, HttpStatus, PANEL_TOKEN_ZERO_TOKEN, UnknownDevice } from "constant";
import {
    convertPrometheusTimeToSeconds,
    monthsToSeconds,
    parseColorToHex,
    totalDoesNotExceedMax,
} from "utils/functions";
import { debounce, get, cloneDeep, uniqBy, upperFirst } from "lodash";
import "bootstrap-daterangepicker/daterangepicker.css";
import { NUMBER_OF_SCROLL } from "pages/Dashboard";
import WidgetBox from "../WidgetBox";
import RefreshRateDropdown from "components/dashboard/RefreshRateDropdown";
import useChartRefreshRate from "hooks/useChartRefreshRate";
import ChartTimeSelectionDropdown from "components/dashboard/ChartTimeSelectionDropdown";
import useSelectChartTimeRange from "hooks/useSelectChartTimeRange";
import useChartWidth from "hooks/useChartWidth";
import { CustomRangeDetails } from "components/dashboard/CustomRangeDetails";
import moment from "moment";
import { getCommonXAxis } from "utils/chart";
import { useDateTimeFormatStore } from "hooks/useDateTimeFormatStore";
const { FORBIDDEN } = HttpStatus;
const FETCH_TIME_PER_PAGE = 500;

// Helper to create Y-axis label formatter
const createYAxisFormatter = () => {
    return (value: any) => {
        // For On/Off states
        return formatBinaryState(value);
    };
};

const formatBinaryState = (value: unknown): string => {
    // In getStackedChartQuery, when the chart is considered not raw data (large time range / too many samples), 
    // it switches to aggregated query mode and runs changeDataTo2DP(...), 
    // which does toFixed(2)
    const n = typeof value === "number" ? value : Number(value);
    return Number.isFinite(n) && n === 1 ? "On" : "Off";
};

const addNoteToChart = (
    isAggregated: boolean,
    option: EChartsOption,
    boxWidth: number
): EChartsOption => {
    const baseTitle = Array.isArray(option.title)
        ? { ...option.title[0] }
        : { ...option.title ? option.title : {} };

    const secondTitle = {
        text: "Averaged data may not reflect actual states. Use a shorter time range for precise state data.",
        left: "15",
        bottom: "1%",
        textStyle: {
            fontSize: 12,
            fontWeight: "normal",
            color: "#828fab",
            fontStyle: "italic",
            width: 2 * boxWidth,
            overflow: "truncate",
            ellipsis: "...",
        },
        show: isAggregated, // toggle visibility
    };

    const update = {
        ...option,
        title: [baseTitle, secondTitle],
        legend: {
            ...option.legend,
            bottom: isAggregated ? 55 : 35,
        },
        grid: {
            ...option.grid,
            bottom: isAggregated ? 160 : 140,
        },

        dataZoom: option.dataZoom?.map((zoom: any) =>
            zoom.type === "slider"
                ? { ...zoom, bottom: isAggregated ? 105 : 85 }
                : zoom
        ),
    };
    return update;
};

const TimeLineWidget = (props: any) => {
    const { dateTimeFormat, longDateTimeFormat } = useDateTimeFormatStore(
        (state) => ({
            dateTimeFormat: state.dateTimeFormat,
            longDateTimeFormat: state.longDateTimeFormat,
        })
    );

    // ----------------- props -----------------
    const {
        panel,
        panel: {
            query_time,
            time_range,
            uuid: panelId,
            refresh_rate,
            attributes,
            name,
        },
        isVisible,
        visibleRef,
        zoomLevel,
        dashboardId,
        index,
        gatewaysConfig,
    } = props;

    const chartRef: any = useRef(null);
    const chartInstance: any = chartRef.current?.getEchartsInstance();
    const getAllYAxis: any = useCallback(() => {
        let allSensorClass = cloneDeep(attributes).map((i: any) => ({
            ...i,
            id: `${i.gateway_id}-${i.device_id}-${i.said}`,
        }));

        return uniqBy(allSensorClass, "id");
    }, [attributes]);

    // ----------------- chart -----------------
    const [chartState, setChartState] = useState<any>({
        series: [],
    });

    const [deviceName, setDeviceName] = useState<string>("");
    const [deviceConfig, setDeviceConfig] = useState<any>({});

    const { boxWidth } = useChartWidth({
        ref: visibleRef,
        occupiedWidth: 200,
    });

    const [isLoading, setIsLoading] = useState(true);
    const [selectedLegend, setSelectedLegend] = useState<
        Record<string, boolean>
    >({});

    // ----------------- refresh rate -----------------
    const {
        refreshRate,
        setRefreshRate,
        editableCustomRate,
        setEditableCustomRate,
    } = useChartRefreshRate(refresh_rate);

    // ----------------- time range -----------------
    const {
        lastTimeRangeSelect,
        setLastTimeRangeSelect,
        getStartDateTime,
        getEndDateTime,
        getTimeGapObject,
    } = useSelectChartTimeRange({
        initial_time_range: time_range,
        initial_query_time: query_time,
        setRefreshRate,
        dashboardId: dashboardId,
        panelId,
    });

    // ----------------- run out of token -----------------
    const [isOutOfPaymentToken, setIsOutOfPaymentToken] = useState(false);

    useEffect(() => {
        let deviceName = "";

        for (let attribute of attributes) {
            const { gateway_id, device_id } = attribute;
            const _device = gatewaysConfig?.[gateway_id]?.find(
                ({ UID }: { UID: string }) => UID === device_id
            );

            deviceName = get(_device, "NICK", UnknownDevice);
            setDeviceName(deviceName);
        }
    }, [attributes, gatewaysConfig]);

    const shouldShowAggregation = (selected_time_value: number) => {
        const sensorReportRate = lastTimeRangeSelect.INTVL;
        const totalSample = Math.floor(selected_time_value / sensorReportRate);
        return (
            selected_time_value > monthsToSeconds(6) ||
            !totalDoesNotExceedMax({ total: totalSample, numberOfChart: 1 })
        );
    };

    const getUpdatedChartConfig = async () => {
        const newLastTimeRangeSelect: any = {};

        const sensorRate = get(deviceConfig, "INTVL", 10);

        newLastTimeRangeSelect.INTVL = sensorRate;
        newLastTimeRangeSelect.showCandlestickChart = false;

        setLastTimeRangeSelect({
            ...lastTimeRangeSelect,
            ...newLastTimeRangeSelect,
        });
        return newLastTimeRangeSelect;
    };

    useEffect(() => {
        const newLastTimeRangeSelect: any = {};
        const firstAttribute = attributes[0];
        const sensor = gatewaysConfig[firstAttribute.gateway_id]?.find(
            ({ UID }: { UID: string }) => UID === firstAttribute.device_id
        );

        const sensorRate = get(sensor, "INTVL", 10);
        setDeviceConfig(sensor);

        newLastTimeRangeSelect.INTVL = sensorRate;
        newLastTimeRangeSelect.showCandlestickChart = false;

        setLastTimeRangeSelect({
            ...lastTimeRangeSelect,
            ...newLastTimeRangeSelect,
            showAggregated: shouldShowAggregation(
                convertPrometheusTimeToSeconds(lastTimeRangeSelect.time_range)
            ),
        });
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gatewaysConfig, lastTimeRangeSelect.time_range]);

    useEffect(() => {
        if (chartInstance) {
            chartInstance.setOption({
                xAxis: {
                    min: Number(getStartDateTime()),
                    max: Number(getEndDateTime()),
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chartInstance, dateTimeFormat, getEndDateTime, getStartDateTime]);

    const buildYAxisConfig = useCallback(() => {
        const allYAxis = getAllYAxis();

        return allYAxis.map((_: any, index: number) => ({
            type: "value",
            alignTicks: false,
            axisLabel: {
                formatter: createYAxisFormatter(),
            },
            min: 0,
            max: 1,
            interval: 1,
        }));
    }, [getAllYAxis]);

    const memoizedYAxisConfig = useMemo(
        () => buildYAxisConfig(),
        [buildYAxisConfig]
    );

    const chartColors = useMemo(() => {
        return attributes.map((attr: any) => parseColorToHex(attr.color));
    }, [attributes]);
    const deviceType: string = useMemo(() => {
        return attributes?.[0]?.name || "";
    }, [attributes]);

    chartInstance?.setOption(
        {
            yAxis: memoizedYAxisConfig,
        },
        { replaceMerge: ["yAxis"] }
    );

    const fetchAll = debounce(async () => {
        if (!isVisible) return;

        panel.attributes = panel.attributes.map((attr: any) => ({
            ...attr,
            isBinary: true,
        }));
        const nameArray = panel.attributes.map(({ said }: any) =>
            said === 0
                ? `${deviceType.capitalize()} On/Off`
                : `${deviceType.capitalize()} Occupancy`
        );

        if (deviceName && deviceName !== UnknownDevice) {
            getStackedChartQuery({
                panels: panel,
                namesAry: nameArray,
                yAxisAry: getAllYAxis(),
                time_range: lastTimeRangeSelect.time_range,
                query_time: lastTimeRangeSelect.query_time,
                gapTime: lastTimeRangeSelect.INTVL * 1000,
            }).then((sampleRes: any) => {
                if (sampleRes.response?.status === FORBIDDEN) {
                    if (sampleRes.config.url.includes(PANEL_TOKEN_ZERO_TOKEN)) {
                        setIsOutOfPaymentToken(true);
                    } else {
                        setIsOutOfPaymentToken(false);
                    }
                }

                const { series = [] }: any = sampleRes || {};

                series.forEach((s: any) => {
                    if (s.itemStyle?.color) {
                        s.itemStyle.color = parseColorToHex(s.itemStyle.color);
                    } else if (s.color) {
                        s.color = parseColorToHex(s.color);
                    }

                    s.step = "end";
                    s.lineStyle = {
                        type: "solid",
                    };

                    s.yAxisMin = 0;
                    s.yAxisMax = 1;
                });

                const newOption = cloneDeep({
                    ...chartState,
                    series
                });

                const chartInstance = chartRef.current?.getEchartsInstance();
                const hasZoomConfig = !!chartInstance?.getOption().dataZoom?.[0];

                // Not expect to happen because the dataZoom was set when initializing the chart
                // At this step, it must be available
                if (hasZoomConfig) {
                    // Retain previous zoom level after data refresh, set the dataZoom would override the current zoom
                    newOption.dataZoom = chartInstance?.getOption().dataZoom;
                } else {
                    Object.assign(newOption, cloneDeep(DEFAULT_ZOOM_OPTION));
                }

                setChartState(newOption);
            });
        }
    }, 100);

    useEffect(() => {
        if (isOutOfPaymentToken) {
            setIsLoading(false);
            return;
        }

        if (deviceName && deviceName !== UnknownDevice) {
            fetchAll();
        }

        if (Number(refreshRate.seconds) === 0) return;

        if (!isOutOfPaymentToken) {
            // delay fetch API
            const delayTimeStamp =
                (FETCH_TIME_PER_PAGE / NUMBER_OF_SCROLL) *
                (index % NUMBER_OF_SCROLL);
            const { INTVL: sensorReportRate } = lastTimeRangeSelect;

            let getChartRefreshRateInSecs = () => {
                if (refreshRate.seconds === "auto") {
                    return sensorReportRate;
                } else {
                    return refreshRate.seconds;
                }
            };

            const interval = setInterval(() => {
                setTimeout(() => {
                    fetchAll();
                }, delayTimeStamp);
            }, getChartRefreshRateInSecs() * 1000);
            return () => clearInterval(interval);
        }

        return () => { };


        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isOutOfPaymentToken,
        lastTimeRangeSelect.time_range,
        lastTimeRangeSelect.INTVL,
        refreshRate.seconds,
        isVisible,
        isLoading,
    ]);

    const resetZoom = useCallback(() => {
        setChartState((chartState: EChartsOption) => ({
            ...chartState,
            dataZoom: DEFAULT_ZOOM_OPTION.dataZoom,
        }));
    }, []);

    const handleTimeSelectionChange = useCallback(
        ({ shouldSetAutoRefresh }: { shouldSetAutoRefresh: boolean }) => {
            // Reset chart zoom when time range changes
            resetZoom();

            if (shouldSetAutoRefresh) {
                setRefreshRate({ seconds: "auto" });
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setRefreshRate, chartInstance]
    );

    const renderSubtext = () => {
        let lastRecordTime: string = '';
        let lastRecordStatus: string = '-';

        if (chartState.series[0]?.data.at(-1)) {
            // sample of a record is
            // [1769594514000, '0']
            // 0 is Off, 1 is On
            const lastRecord = chartState.series[0]?.data.at(-1);

            lastRecordTime = moment(lastRecord[0]).format(longDateTimeFormat);
            lastRecordStatus = lastRecord[1] === '1' ? 'On' : 'Off';
        }

        const type = upperFirst(deviceType)
        return `{s1|${type} Status}\n{s1|${type}:} {s3|${deviceName}}\n{s4|${lastRecordStatus}}${lastRecordTime
            ? `\n{s1|${lastRecordTime}}`
            : ""
            }`;
    };

    const getYAxisOptions = () => buildYAxisConfig();

    const option: any = {
        ...chartState,
        backgroundColor: "#2F3A51",
        responsive: true,
        maintainAspectRatio: false,
        tooltip: {
            trigger: "axis",
            renderMode: "html",
            appendTo: "body",
            valueFormatter: (value: any) => {
                const parsedValue = Array.isArray(value) ? value[1] : value;
                return formatBinaryState(parsedValue);
            },
            axisPointer: {
                label: {
                    formatter: (params: any) => {
                        return `${moment(params.value, "x").format(
                            longDateTimeFormat
                        )}`;
                    },
                },
            },
        },
        title: {
            show: true,
            left: 14,
            top: 15,
            text: name || "Room Timeline",
            textStyle: {
                color: "#ffffff",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 18,
                overflow: "truncate",
                ellipsis: "...",
                width: boxWidth,
            },
            subtext: renderSubtext(),
            subtextStyle: {
                color: "#828FAB",
                fontFamily: "Roboto",
                fontWeight: 400,
                fontSize: 14,
                overflow: "truncate",
                ellipsis: "...",
                width: boxWidth + 150,
                rich: {
                    s1: {
                        color: "#828fab",
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: 20,
                    },
                    s2: {
                        color: "#828fab",
                        fontSize: 18,
                    },
                    s3: {
                        color: "white",
                        fontSize: 16,
                        lineHeight: 20,
                        fontFamily: "Roboto",
                    },
                    s4: {
                        color: chartColors[0] ?? "#f2994a",
                        fontSize: 18,
                        fontWeight: 600,
                        fontFamily: "Roboto",
                        padding: [chartColors[1] ? 16 : 8, 0, 5, 0]
                    },
                    s5: {
                        color: chartColors[1] ?? "##e86037",
                        fontWeight: 600,
                        fontSize: 18,
                        fontFamily: "Roboto",
                        align: "right",
                        padding: [16, 0, 5, 0]
                    },
                    s6: {
                        color: "#828fab",
                        fontFamily: "Roboto",
                        fontWeight: 400,
                        fontSize: 14,
                        lineHeight: 20,
                        align: "right",
                    },
                },
            },
        },
        toolbox: {
            feature: {
                restore: {},
            },
            top: 12,
            right: 50,
            emphasis: {
                iconStyle: {
                    textPosition: "top",
                    textPadding: -4,
                },
            },
            iconStyle: {
                borderColor: "#00CBAF",
            },
        },
        legend: {
            type: "scroll",
            selectedMode: true,
            left: 15,
            bottom: 40,
            textStyle: {
                color: "#ffffff",
                fontFamily: "Roboto",
                fontWeight: 400,
            },
            pageTextStyle: {
                color: "#ffffff",
            },
            pageIconInactiveColor: "#6c6d6e",
            pageIconColor: "#c8c6c6",
            selected: selectedLegend,
            data: (chartState.series ?? []).map((s: any) => {
                return {
                    name: s.name,
                    itemStyle: {
                        color: s.color,
                    },
                };
            }),
        },
        grid: {
            left: "50px",
            top: "160px",
            right: "50px",
            bottom: "160px",
        },
        xAxis: {
            type: "time",
            boundaryGap: false,
            axisLine: {
                lineStyle: {
                    color: "#4B5569",
                },
            },
            splitNumber: "3",
        },
        yAxis: getYAxisOptions(),
        series: chartState.series,
    };

    const updatedOption = addNoteToChart(
        lastTimeRangeSelect.showAggregated,
        option,
        boxWidth
    );

    const chartOptions = useMemo(() => {
        return {
            ...updatedOption,
            xAxis: getCommonXAxis(option),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updatedOption]);

    return (
        <WidgetBox
            ref={visibleRef}
            className={
                zoomLevel <= 0.5
                    ? "widget-box w-100 h-30vh"
                    : "widget-box w-100"
            }
        >
            <div className="widget">
                <ChartTimeSelectionDropdown
                    lastTimeRangeSelect={lastTimeRangeSelect}
                    setLastTimeRangeSelect={setLastTimeRangeSelect}
                    getStartDateTime={getStartDateTime}
                    getEndDateTime={getEndDateTime}
                    dashboardId={dashboardId}
                    panelId={panelId}
                    getTimeGapObject={getTimeGapObject}
                    getUpdatedChartConfig={getUpdatedChartConfig}
                    onChange={handleTimeSelectionChange}
                />
                {lastTimeRangeSelect.query_time ? (
                    <CustomRangeDetails
                        lastTimeRangeSelect={lastTimeRangeSelect}
                    />
                ) : (
                    <></>
                )}
                <div
                    className="widget-tool"
                    style={{
                        bottom: lastTimeRangeSelect.showAggregated
                            ? "30px"
                            : "12px",
                    }}
                >
                    <RefreshRateDropdown
                        refreshRate={refreshRate}
                        setRefreshRate={setRefreshRate}
                        editableCustomRate={editableCustomRate}
                        setEditableCustomRate={setEditableCustomRate}
                        dashboardId={dashboardId}
                        panelId={panelId}
                        lastTimeRangeSelect={lastTimeRangeSelect}
                    />
                </div>
                {!isLoading && isVisible && (
                    <ReactECharts
                        ref={chartRef}
                        key={panelId}
                        className="chart-box"
                        option={chartOptions}
                        theme={"dark"}
                        lazyUpdate={true}
                        onChartReady={(chartInstance) => {
                            chartInstance.getZr().on("click", (a: any) => {
                                if (a?.target?.__title === "Restore") {
                                    resetZoom();
                                    fetchAll();
                                }
                            });
                            chartInstance.on(
                                "legendselectchanged",
                                function (changes: any) {
                                    setSelectedLegend(changes.selected);
                                }
                            );
                        }}
                    />
                )}
            </div>
        </WidgetBox>
    );
};

export default memo(TimeLineWidget);
